import React, { useCallback } from "react";
import DynamicLinesDialog from "../../../components/DynamicLinesDialog";
import {
  graphqlQuery,
    fetchDataEngine,
  generateAndExecuteMutation,
} from "../../../data/Axios/DynamicService";
import { accountsmanagement, clientManagementUrl } from "../../../config";
import {
  allSuppliers,
  allPayeeTypes,
} from "../../../data/Axios/queries";

const base_url = accountsmanagement.uri;
const client_base_url = clientManagementUrl.uri;

const ManagePaymentVoucherLinesDialog = ({ open, onClose, paymentData, refetchData }) => {
  // Options loaders
  const loadPayeeTypes = useCallback(async () => {
    const data = await graphqlQuery(allPayeeTypes, base_url);
    return data || [];
  }, []);

const loadPayees = useCallback(async (payeeTypeId) => {
  if (!payeeTypeId) return [];

  const typeId = Number(payeeTypeId);

  switch (typeId) {
    case 1: {
      // Supplier
      const suppliers = await graphqlQuery(allSuppliers, base_url);

      return (suppliers || []).map((s) => ({
        id: s.id,
        name: s.supplierName,
      }));
    }

    case 2: {
      // Customer
      // Legacy form initially loads customers as the payees.
      const individualCustomers = await fetchDataEngine(
        "allIndividualClients",
        "clientModel",
        { isActive: 1 },
        ["id", "firstName", "lastName"],
        client_base_url
      );

      return (individualCustomers || []).map((c) => ({
        id: c.id,
        name: `${c.firstName} ${c.lastName}`,
      }));
    }

    case 3: {
      // Employee
      // Employee loading was expected by the legacy filter,
      // but the actual employee-fetching query is not present
      // in the legacy code you provided.
      return [];
    }

    case 4: {
      // GL Account
      const decodedToken = JSON.parse(
        localStorage.getItem("decodedToken") || "{}"
      );

      const companyid = parseInt(decodedToken.CompanyId) || null;

      const glAccountData = await fetchDataEngine(
        "allAccountsByColumnValues",
        "accounts",
        {
          identifierId: companyid,
          identifierType: 7,
          accountType: "Expenses",
        },
        ["id", "accountName"],
        base_url
      );

      return (glAccountData || []).map((gl) => ({
        id: gl.id,
        name: gl.accountName,
      }));
    }

    default:
      return [];
  }
}, []);

  // Form fields configuration
  const formFields = [
    {
      name: "payeeTypeId",
      label: "Payee Type",
      type: "select",
    },
    {
      name: "payeeId",
      label: "Payee",
      type: "dependent-select",
      dependsOn: "payeeTypeId",
    },
    {
      name: "description",
      label: "Description",
      type: "text",
    },
    {
      name: "amount",
      label: "Amount",
      type: "number",
    },
  ];

  // Table columns configuration
  const columns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    { field: "payeeTypeName", headerName: "Payee Type", flex: 1 },
    { field: "payeeName", headerName: "Payee", flex: 1.5 },
    { field: "description", headerName: "Description", flex: 2 },
    { field: "amount", headerName: "Amount", flex: 1, format: "currency" },
  ];

  // Query configuration
  const queryConfig = {
    name: "paymentVoucherLinesView",
    modelName: "paymentVoucherLines",
    inputField: "paymentVoucherId",
    fields: [
      "id",
      "payeeTypeId",
      "payeeTypeName",
      "payeeId",
      "payeeName",
      "description",
      "amount",
    ],
  };

  // Mutation configuration
  const mutationConfig = {
    create: "createPaymentVoucherLine",
    update: "updatePaymentVoucherLine",
    modelName: "newPaymentVoucherLine",
    updateModelName: "updatePaymentVoucherLine",
  };

  // Options loaders map
  const optionsLoaders = {
    payeeTypeId: loadPayeeTypes,
    payeeId: loadPayees,
  };

  return (
    <DynamicLinesDialog
      open={open}
      onClose={onClose}
      title="Manage Payment Voucher Lines"
      parentData={paymentData}
      refetchData={refetchData}
      parentIdField="paymentVoucherId"
      baseUrl={base_url}
      queryConfig={queryConfig}
      mutationConfig={mutationConfig}
      formFields={formFields}
      columns={columns}
      optionsLoaders={optionsLoaders}
    />
  );
};

export default ManagePaymentVoucherLinesDialog;
